import { IObjectStringKeyMap } from '@shared/models';
import { AccountTypes, ExternalEventSourceType, Permission } from '@services';

export enum PropertyExpressionErrorType {
  Property = 'property',
  Syntax = 'syntax',
  Calculation = 'calculation',
  Database = 'database'
}

export enum PropertyExpressionErrorCode {
  SelfRereference = 'selfReference',
  IndirectSelfRereference = 'indirectSelfReference',
  UndefinedProperty = 'undefinedProperty',
  InactiveProperty = 'inactiveProperty',
  IllegalFunctionReference = 'illegalFunctionReference',
  InvalidCharacters = 'invalidCharacters',
  CalculationError = 'calculationError',
  UniqueNameExists = 'uniqueNameExists',
  ReservedUniqueName = 'reservedUniqueName',
}

// Names a calculation formula claims for itself, which the API refuses as a
// property's unique name (CVX-6410). Compared lower-cased, as the API does.
export const RESERVED_PROPERTY_NAMES = ['this'];

export interface PropertyExpressionError {
  type: PropertyExpressionErrorType;
  code?:  PropertyExpressionErrorCode;
  error?: string;
  propertyID: number;
  uniqueName?: string;
}

export enum LimitType {
  Healthy = 'healthy',
  Strained = 'strained'
}

export enum ThresholdType {
  Toggle = 'boolean',
  Selector = 'selector',
  Ascending = 'ascendingNumeric',
  Descending = 'descendingNumeric',
  Interval = 'timeInterval'
}

export enum PropertyMethod {
  Latest = 'latest',
  Highest = 'highest',
  Lowest = 'lowest',
  Cumulative = 'cumulative',
  Average = 'average',
  Delta = 'delta'
}

export interface IThresholdLimit {
  type: LimitType;
  limit: number;
  limit_unit?: string;
}

export interface IThresholds {
  type: ThresholdType;
  limits: IThresholdLimit[];
}

export enum PropertyStyle {
  Numeric = 'number',
  Text = 'text',
  TextArea = 'textarea',
  Note = 'note',
  Flipswitch = 'flipswitch',
  Duration = 'duration',
  Date = 'date',
  Timestamp = 'timestamp',
  Measurement = 'measurement',
  Counter = 'counter',
  UpDown = 'upDown',
  UpStrainedDown = 'upStrainedDown'
}

export enum PropertyCategory {
  Text = 'text',
  Numeric = 'number',
  InService = 'asset:inService',
  InUse = 'asset:inUse',
  MissedCheck = 'check:missed',
  SkippedCheck = 'check:skipped',
  CompletedCheck = 'check:completed',
  FailedCheck = 'check:failed',
  PassedCheck = 'check:passed',
  OpenObservations = 'observation:open',
  UnassignedObservations = 'observation:unassigned',
  Zone = 'zone',
  Timestamp = 'timestamp',
  ContentAvailable = 'content:isPublished',
  ReapprovalDate = 'content:reapprove',
  ReviewDate = 'content:review',
  // ReviewsCompleted = 'content:reviewsCompleted',
  // ReviewsMissing = 'content:reviewsMissing'
}

export enum PropertySource {
  Manual = 'manual',
  Hybrid = 'hybrid',
  Derived = 'derived',
  TimeUntil = 'timeUntil',
  OverTime = 'overTime',
  Pull = 'pull',
  Push = 'push',
  PushOverTime = 'pushOverTime',
  Calculated = 'calculated'
}

export enum PropertySubject {
  Asset = 'asset',
  Worker = 'worker',
  Zone = 'zone',
  Location = 'location',
  Content = 'content'
}

export interface IPropertyEvent {
  time: number;
  userID: number;
  subject: PropertySubject;
  subjectID: string;
  activity: string;
  value: PropertyEventValue;
  id: number;
  propertyID: number;
}

export type PropertyEventValue = IPropertyEventValue | string;

export interface IPropertyEventValue {
  value: IObjectStringKeyMap<string> | string;
  style: PropertyStyle;
  note?: string;
}

export interface IPropertyEventMeasurementValue extends IPropertyEventValue {
  units: string;
}

export interface IPropertyEventDurationValue extends IPropertyEventValue {
  value: string;
  value_unit: string;
}

export interface IPropertyEventActivity {
  from: string;
  id: number;
  type: string;
}

export type PropertyEventActivity = IPropertyEventActivity | string;

export interface IPropertyEventSource {
  sourceType: ExternalEventSourceType,
  itemID: number
}

export interface IPropertyHistoryEntry {
  time: number;
  userID: number;
  activity: string;
  notes: string;
}

export interface IProperty {
  propertyID: number;
  lastUpdate: number;
  addedBy: number;
  addedAt: number;
  active: number;
  hidden?: boolean;
  healthRelated: number;
  thresholds: IThresholds;
  canAggregate: number;
  subjects: PropertySubject[];
  source: PropertySource;
  method: PropertyMethod;
  timespan: number;
  category: PropertyCategory;
  uniqueName: string;
  style: PropertyStyle;
  folderID: number;
  title: string;
  description: string;
  unit: string;
  displayPrecision?: number; // -1 for full precision
  events: IPropertyEvent[];
  history: IPropertyHistoryEntry[];
  disabledAt: number;
  disabledBy: number;
  static: number;
  defaultValue?: PropertyDefaultValue;
  manualInput?: PropertyManualInput;
  eventSource?: IPropertyHistoryEntry;
  translations?: any;
}

export type PropertyDefaultValue = string | number;

export interface PropertyManualInput {
  observations: {
    add: PropertyManualInputObservation;
    fix: PropertyManualInputObservation;
  },
  addEvent: PropertyManualInputEvent;
  label: {
    [language: string]: string;
  },
  settings?: PropertyManualSettings;
}

export interface PropertyManualInputBase {
  enabled: number;
  permissions: {
    types: AccountTypes[];
    permissions: Permission[];
    teams: number[];
    users: string[];
  }
}

export interface PropertyManualSettings {
  units: string;
  startValue: string;
  increment: string;
  fractionIncrement: string;
}

export interface PropertyManualInputObservation extends PropertyManualInputBase {
  required: string;
}

export interface PropertyManualInputEvent extends PropertyManualInputBase {
  note: 'disabled' | 'optional' | 'required';
}

export interface IPropertyEventRequestsParams {
  propertyID?: number;
  uniqueName?: string;
  subject?: PropertySubject;
  subjectID?: number;
  activity?: string;
  value?: string | IPropertyEventValue;
  events?: IPropertyEventRequestParams[];
}

export interface IPropertyEventRequestParams {
  value: string;
  subject: PropertySubject;
  subjectID: number;
  activity: string;
}

export interface IPropertyMeta {
  propertyID: number;
  propertyFolderID?: number;
  healthRelated: number;
  hidden?: boolean;
  defaultValue?: PropertyDefaultValue;
}
